/**
 * The guest flow, on one screen. build-prompt section 6.
 *
 * One screen rather than three, because the target is a confirmed RSVP in
 * under 60 seconds on a phone. Every tap saves immediately, so a family that
 * gets interrupted halfway has still told the host something useful.
 */

import { useEffect, useState } from 'react';
import { guest, type InvitationView, type Person, type RsvpState } from '../api.js';
import { calendarUid, downloadIcs } from '../ics.js';

const ROLE_LABEL: Record<string, string> = {
  anchor_child: 'criança convidada',
  mother: 'mãe',
  father: 'pai',
  sibling: 'irmão ou irmã',
  guardian: 'responsável',
  nanny: 'babá',
  other: '',
};

/** Roles we ask an age for. Ages come from here, not from the spreadsheet. */
const CHILD_ROLES = new Set(['anchor_child', 'sibling']);

export function Invitation({ token }: { token: string }) {
  const [view, setView] = useState<InvitationView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    guest
      .invitation(token)
      .then(setView)
      .catch((err: Error) => setError(err.message));
  }, [token]);

  if (error) {
    return (
      <div className="page">
        <div className="banner error">{error}</div>
        <p className="muted">
          Se você recebeu este link de alguém, peça para a pessoa reenviar.
        </p>
      </div>
    );
  }

  if (!view) return <div className="spinner">Carregando…</div>;

  const { event, household, people } = view;

  /** Optimistic: the tap lands immediately, the request follows. */
  async function update(
    personId: string,
    changes: Partial<Pick<Person, 'rsvpState' | 'ageYears'>>,
  ) {
    setView((current) =>
      current === null
        ? current
        : {
            ...current,
            people: current.people.map((p) =>
              p.personId === personId ? { ...p, ...changes } : p,
            ),
          },
    );

    if (changes.rsvpState === undefined && changes.ageYears === undefined) return;

    setSaving((n) => n + 1);
    try {
      const person = people.find((p) => p.personId === personId);
      await guest.rsvp(
        token,
        personId,
        changes.rsvpState ?? person?.rsvpState ?? 'pending',
        changes.ageYears,
      );
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving((n) => n - 1);
    }
  }

  const answered = people.filter((p) => p.rsvpState !== 'pending').length;
  const coming = people.filter(
    (p) => p.rsvpState === 'attending_staying' || p.rsvpState === 'attending_dropoff',
  );

  if (done) {
    return (
      <Done
        view={view}
        token={token}
        onEdit={() => setDone(false)}
        coming={coming}
      />
    );
  }

  return (
    <div className="page">
      <Hero event={event} />

      {/* Hers to everyone first, then the line written for this family. */}
      {event.message && <div className="note">{event.message}</div>}
      {household.guestMessage && (
        <div className="note note-personal">{household.guestMessage}</div>
      )}

      <h2>Quem vem da família de {household.label}?</h2>
      <p className="muted">
        Marque cada pessoa. Suas respostas são salvas na hora, e tocar de novo
        na mesma resposta desmarca.
      </p>

      {error && <div className="banner error">{error}</div>}

      <div className="card">
        {people.map((person) => (
          <PersonRow key={person.personId} person={person} onChange={update} />
        ))}
      </div>

      <AddPerson
        token={token}
        onAdded={(person) =>
          setView((current) =>
            current === null
              ? current
              : { ...current, people: [...current.people, person] },
          )
        }
      />

      <Dietary token={token} household={household} />

      <div className="sticky-action">
        <button
          className="primary"
          onClick={() => setDone(true)}
          disabled={answered === 0}
        >
          {answered === 0
            ? 'Marque pelo menos uma pessoa'
            : saving > 0
              ? 'Salvando…'
              : 'Pronto'}
        </button>
      </div>
    </div>
  );
}

function Hero({ event }: { event: InvitationView['event'] }) {
  return (
    <div className="invite-hero">
      {event.artUrl && (
        <img src={event.artUrl} alt="" className="art-hero" loading="eager" />
      )}
      <div className="age">{event.ageTurning} anos</div>
      <h1>{event.childName}</h1>
      <div className="when">{event.whenText}</div>
      <div className="muted">{event.venue}</div>
      <div className="deadline">Confirme até {event.deadlineText}</div>
      {event.venueMapsUrl && (
        <p style={{ marginTop: '0.75rem' }}>
          <a href={event.venueMapsUrl} target="_blank" rel="noreferrer">
            Ver no mapa
          </a>
        </p>
      )}
    </div>
  );
}

function PersonRow({
  person,
  onChange,
}: {
  person: Person;
  onChange: (
    personId: string,
    changes: Partial<Pick<Person, 'rsvpState' | 'ageYears'>>,
  ) => void;
}) {
  const attending =
    person.rsvpState === 'attending_staying' ||
    person.rsvpState === 'attending_dropoff';

  const isChild = CHILD_ROLES.has(person.role);

  // Ask for an age only when it matters: a child who is coming and whose age
  // we do not know. The buffet count needs it; nobody else does.
  const asksAge = attending && isChild;

  const role = ROLE_LABEL[person.role] ?? '';

  /**
   * Tapping the answer she already gave takes it back.
   *
   * There was no way to undo: a misplaced tap on "não vem" was permanent, and
   * the only escape was to answer something else and mean it.
   */
  function pick(state: Person['rsvpState']) {
    onChange(person.personId, {
      rsvpState: person.rsvpState === state ? 'pending' : state,
    });
  }

  return (
    <div className="person">
      <div className="person-name">
        {person.name}
        {role && <span className="person-role"> · {role}</span>}
      </div>

      {/*
        * "Deixa e volta" is only offered to grown-ups. A child does not drop
        * herself off: the answer belongs to whoever brings her, and offering
        * it on the child's row made the question unanswerable.
        */}
      <div className={isChild ? 'choices two' : 'choices'}>
        <Choice
          className="stay"
          active={person.rsvpState === 'attending_staying'}
          onClick={() => pick('attending_staying')}
        >
          {isChild ? 'Vem' : 'Vem e fica'}
        </Choice>
        {!isChild && (
          <Choice
            className="drop"
            active={person.rsvpState === 'attending_dropoff'}
            onClick={() => pick('attending_dropoff')}
          >
            Deixa e volta
          </Choice>
        )}
        <Choice
          className="no"
          active={person.rsvpState === 'not_attending'}
          onClick={() => pick('not_attending')}
        >
          Não vem
        </Choice>
      </div>

      {asksAge && (
        <div className="age-row">
          <label htmlFor={`age-${person.personId}`} style={{ margin: 0 }}>
            Idade
          </label>
          <input
            id={`age-${person.personId}`}
            className="age"
            type="number"
            inputMode="numeric"
            min={0}
            max={21}
            value={person.ageYears ?? ''}
            onChange={(e) =>
              onChange(person.personId, {
                ageYears: e.target.value === '' ? null : Number(e.target.value),
              })
            }
          />
          <span className="muted">ajuda a organizar o bolo</span>
        </div>
      )}
    </div>
  );
}

function Choice({
  active,
  className,
  onClick,
  children,
}: {
  active: boolean;
  className: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`choice ${className}`}
      aria-pressed={active}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function AddPerson({
  token,
  onAdded,
}: {
  token: string;
  onAdded: (person: Person) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState('other');
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <button className="block" onClick={() => setOpen(true)}>
        + Adicionar alguém que não está na lista
      </button>
    );
  }

  return (
    <div className="card">
      <label htmlFor="new-name">Nome</label>
      <input
        id="new-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Ex.: Vovó Ana"
        autoFocus
      />

      <label htmlFor="new-role">Quem é</label>
      <select id="new-role" value={role} onChange={(e) => setRole(e.target.value)}>
        <option value="sibling">Irmão ou irmã</option>
        <option value="mother">Mãe</option>
        <option value="father">Pai</option>
        <option value="guardian">Responsável</option>
        <option value="nanny">Babá</option>
        <option value="other">Outro</option>
      </select>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
        <button
          className="primary"
          disabled={name.trim() === '' || busy}
          onClick={async () => {
            setBusy(true);
            try {
              const { person } = await guest.addPerson(token, {
                name,
                role,
                state: 'attending_staying',
              });
              onAdded(person);
              setName('');
              setOpen(false);
            } finally {
              setBusy(false);
            }
          }}
        >
          Adicionar
        </button>
        <button onClick={() => setOpen(false)}>Cancelar</button>
      </div>
    </div>
  );
}

function Dietary({
  token,
  household,
}: {
  token: string;
  household: InvitationView['household'];
}) {
  const [has, setHas] = useState<boolean | null>(household.hasDietaryRestriction);
  const [note, setNote] = useState(household.dietaryNote ?? '');

  async function save(next: boolean | null, nextNote: string) {
    setHas(next);
    await guest.dietary(token, next, nextNote).catch(() => undefined);
  }

  return (
    <div className="card">
      <strong>Alguém da família tem restrição alimentar?</strong>
      <div className="choices" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <button
          type="button"
          className="choice stay"
          aria-pressed={has === true}
          onClick={() => save(true, note)}
        >
          Sim
        </button>
        <button
          type="button"
          className="choice no"
          aria-pressed={has === false}
          onClick={() => save(false, '')}
        >
          Não
        </button>
      </div>

      {has === true && (
        <>
          <label htmlFor="diet-note">Qual?</label>
          <input
            id="diet-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => save(true, note)}
            placeholder="Ex.: sem lactose"
          />
        </>
      )}
    </div>
  );
}

function Done({
  view,
  token,
  coming,
  onEdit,
}: {
  view: InvitationView;
  token: string;
  coming: Person[];
  onEdit: () => void;
}) {
  const { event } = view;

  return (
    <div className="page">
      <div className="banner ok">Prontinho! A anfitriã já recebeu sua resposta.</div>

      <Hero event={event} />

      <h2>Sua resposta</h2>
      <div className="card">
        {coming.length === 0 ? (
          <p className="muted">Ninguém da família vai.</p>
        ) : (
          coming.map((person) => (
            <div className="row" key={person.personId}>
              <div className="grow">
                <div className="label-main">{person.name}</div>
              </div>
              <span className={`tag ${person.rsvpState === 'attending_staying' ? 'ok' : 'warn'}`}>
                {person.rsvpState === 'attending_staying' ? 'fica' : 'deixa e volta'}
              </span>
            </div>
          ))
        )}
      </div>

      <button
        className="block"
        onClick={() => downloadIcs(event, calendarUid(event.slug, token))}
      >
        Adicionar ao calendário
      </button>

      <p className="muted" style={{ marginTop: '0.75rem' }}>
        Baixa um arquivo que você abre no seu calendário. Não pedimos seu e-mail
        e não guardamos nada seu.
      </p>

      <button className="block" onClick={onEdit} style={{ marginTop: '1rem' }}>
        Mudar minha resposta
      </button>

      <p className="muted" style={{ marginTop: '1rem' }}>
        Guarde este link: dá para voltar e mudar quando quiser, até{' '}
        {event.deadlineText}.
      </p>
    </div>
  );
}
